import { BaseService } from '../base/BaseService';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';

type AIPromptStarterRow = Database['public']['Tables']['content_templates']['Row'];

export interface AIPromptStarterFilters {
  category?: string;
}

export class AIPromptStarterService extends BaseService {
  private static instance: AIPromptStarterService;

  private constructor() {
    super({
      defaultTTL: 60 * 60 * 1000, // 1 hour for AI prompt starters
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): AIPromptStarterService {
    if (!AIPromptStarterService.instance) {
      AIPromptStarterService.instance = new AIPromptStarterService();
    }
    return AIPromptStarterService.instance;
  }

  /**
   * Get all AI prompt starters with optional filters
   */
  async getPromptStarters(
    filters?: AIPromptStarterFilters,
    options?: QueryOptions,
  ): Promise<ServiceResponse<AIPromptStarterRow[]>> {
    return this.execute(async () => {
      const cacheKey = `ai-prompt-starters-${JSON.stringify(filters)}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          let query = this.client
            .from('content_templates')
            .select('*')
            .eq('template_type', 'ai_prompt');

          // Apply filters
          if (filters?.category) {
            query = query.eq('category', filters.category);
          }

          // Apply ordering
          query = query
            .order('category', { ascending: true })
            .order('prompt_text', { ascending: true });

          // Apply query options if provided
          if (options) {
            query = this.buildQuery(query, options);
          }

          const { data, error } = await query;

          if (error) throw error;

          return data || [];
        },
        options,
      );
    });
  }

  /**
   * Get prompt starters by category
   */
  async getPromptStartersByCategory(
    category: string,
  ): Promise<ServiceResponse<AIPromptStarterRow[]>> {
    return this.getPromptStarters({ category });
  }

  /**
   * Get all unique categories
   */
  async getCategories(): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      const cacheKey = 'ai-prompt-starter-categories';

      return this.executeWithCache(
        cacheKey,
        async () => {
          const { data, error } = await this.client
            .from('content_templates')
            .select('category')
            .eq('template_type', 'ai_prompt')
            .not('category', 'is', null);

          if (error) throw error;

          // Extract unique categories
          const categories = new Set<string>();
          (data || []).forEach((item) => {
            if (item.category) {
              categories.add(item.category);
            }
          });

          return Array.from(categories).sort();
        },
        { ttl: 30 * 60 * 1000 }, // 30 minutes for categories
      );
    });
  }

  /**
   * Clear all AI prompt starter caches
   */
  clearAllCaches(): void {
    const cacheStore = (BaseService as any).cacheStore as Map<string, any>;
    for (const key of cacheStore.keys()) {
      if (key.startsWith('ai-prompt-starter')) {
        this.clearCache(key);
      }
    }
  }
}

// Export singleton instance
export const aiPromptStarterService = AIPromptStarterService.getInstance();
